import React, { useRef, useState, useEffect } from 'react';
import PropTypes, { shape } from 'prop-types';
import { propTypes } from '../../util/types';
import { FormattedMessage } from '../../util/reactIntl';
import {
  windowDefined,
  getElementHeight,
  useVideoPlayer,
  useVideoPlayerEvents,
} from './VideoPlayer.helpers';
import classNames from 'classnames';

import Controller, { ControllerOverlay } from './Controller';
import BigPlayButton from './BigPlayButton';
import css from './VideoPlayer.module.css';
import AspectRatioWrapper from '../AspectRatioWrapper/AspectRatioWrapper';

// Settings
const PRELOAD_VIDEO = 'auto';
const SHOW_PLAYER_TIME = true;
const SHOW_BIG_PLAY_BUTTON = false;
const PLAYER_DISABLED = false;
const HOVER_DELAY = 500;
const ENABLE_HOVER_PLAY = true;

const VideoPlayer = props => {
  const {
    className,
    rootClassName,
    videoWrapperClassName,
    videoClassName,
    controlsClassName,
    controlsTextClassName,
    video,
    settings,
    aspectWidth,
    aspectHeight,
    useNaturalSize,
  } = props;

  // Hide the component if window or document object isn't
  // already defined, to avoid causing SSR server error
  if (!windowDefined) {
    return null;
  }

  const videoRef = useRef(null);
  const controllerRef = useRef(null);
  const seekTrackRef = useRef(null);
  const fullscreenElementRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const wasPlayingBeforeHoverRef = useRef(false);

  const player = useVideoPlayer();
  const playerEvents = useVideoPlayerEvents(player);

  const [isHovering, setIsHovering] = useState(false);

  // Player settings
  const showPlayerTime = settings.playerTime ?? SHOW_PLAYER_TIME;
  const showBigPlayButton = settings.bigPlayButton && !player.playing && !player.paused;
  const enableHoverPlay = settings.hoverPlay ?? ENABLE_HOVER_PLAY;
  const hoverDelay = settings.hoverDelay ?? HOVER_DELAY;

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Handle hover auto-play
  const onMouseEnter = () => {
    if (!enableHoverPlay || player.error || !player.canPlay || settings.disabled) {
      return;
    }

    setIsHovering(true);

    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Set timeout for delayed autoplay
    hoverTimeoutRef.current = setTimeout(() => {
      if (videoRef.current && !player.playing) {
        // Store whether video was playing before hover
        wasPlayingBeforeHoverRef.current = player.playing;

        // Ensure video is muted for autoplay to work
        if (!player.muted) {
          player.onMute(true);
        }

        // Reset video to beginning if it ended
        if (videoRef.current.ended) {
          videoRef.current.currentTime = 0;
        }

        videoRef.current.play();
      }
    }, hoverDelay);
  };

  const onMouseLeave = () => {
    if (!enableHoverPlay) {
      return;
    }

    setIsHovering(false);

    // Clear timeout if mouse leaves before delay
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Stop video if it was started by hover
    if (videoRef.current && player.playing && !wasPlayingBeforeHoverRef.current) {
      videoRef.current.pause();
      // Reset to beginning for next hover
      videoRef.current.currentTime = 0;
    }
  };

  // Player classNames
  const classes = classNames(rootClassName || css.root, className, {
    [css.disabledRoot]: settings.disabled || !player.canPlay,
    [css.bigPlayButtonRoot]: showBigPlayButton,
    [css.fullscreenRoot]: player.fullscreen,
    [css.hoveringRoot]: isHovering && enableHoverPlay, // Add hovering class
    [css.playingRoot]: player.playing, // Add playing class for CSS
  });

  const videoWrapperClasses = classNames(css.videoPlayerWrapper, videoWrapperClassName);
  const videoClasses = classNames(css.videoPlayer, videoClassName);
  const controlsClasses = classNames(css.controls, controlsClassName);
  const controlsTextClasses = classNames(css.controlsText, controlsTextClassName);

  if (!video || !video.src) {
    const loadingClasses = classNames(css.root, css.loadingRoot);
    return (
      <div className={loadingClasses}>
        <AspectRatioWrapper
          className={videoWrapperClasses}
          aspectPaddingClassName={css.aspectPadding}
          width={aspectWidth}
          height={aspectHeight}
        >
          <span className={css.tag}>
            <FormattedMessage id="VideoPlayer.noVideoMessage" />
          </span>
        </AspectRatioWrapper>
      </div>
    );
  } else if (player.error) {
    const errorClasses = classNames(css.root, css.errorRoot);
    return (
      <div className={errorClasses}>
        <AspectRatioWrapper
          className={videoWrapperClasses}
          aspectPaddingClassName={css.aspectPadding}
          width={aspectWidth}
          height={aspectHeight}
        >
          <span className={css.tag}>
            <FormattedMessage id="VideoPlayer.videoPlayerError" />
          </span>
        </AspectRatioWrapper>
      </div>
    );
  }

  const onTogglePlay = () => {
    // If user manually clicks play, mark that it wasn't started by hover
    wasPlayingBeforeHoverRef.current = true;

    if (player.playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  // Video element related props that are required
  // so that the component can properly work
  const videoProps = {
    ref: videoRef,
    muted: player.muted,
    preload: PRELOAD_VIDEO,

    // events
    onLoadedMetadata: playerEvents.onLoadedMetadata,
    onCanPlay: playerEvents.onCanPlay,
    onPlaying: playerEvents.onPlaying,
    onPause: playerEvents.onPause,
    onTimeUpdate: playerEvents.onTimeUpdate,
    onEnded: playerEvents.onEnded,
    onSuspend: playerEvents.onSuspend,
    onError: playerEvents.onError,
  };

  const videoContent = (
    <>
      <BigPlayButton show={showBigPlayButton} />
      <video className={videoClasses} playsInline {...videoProps}>
        <source src={`${video.src}#t=0.001`} />
      </video>
    </>
  );

  return (
    <div
      className={classes}
      ref={fullscreenElementRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {useNaturalSize ? (
        <div className={videoWrapperClasses} onClick={onTogglePlay}>
          {videoContent}
        </div>
      ) : (
        <AspectRatioWrapper
          className={videoWrapperClasses}
          aspectPaddingClassName={css.aspectPadding}
          width={aspectWidth}
          height={aspectHeight}
          onClick={onTogglePlay}
        >
          {videoContent}
        </AspectRatioWrapper>
      )}
      <ControllerOverlay height={getElementHeight(controllerRef?.current)} />
      <Controller
        controlsClasses={controlsClasses}
        controlsTextClasses={controlsTextClasses}
        player={player}
        showPlayerTime={showPlayerTime}
        videoRef={videoRef}
        controllerRef={controllerRef}
        seekTrackRef={seekTrackRef}
        fullscreenElementRef={fullscreenElementRef}
        onTogglePlay={onTogglePlay}
      />
    </div>
  );
};

VideoPlayer.defaultProps = {
  className: null,
  rootClassName: null,
  videoWrapperClassName: null,
  videoClassName: null,
  controlsClassName: null,
  controlsTextClassName: null,
  aspectWidth: 5,
  aspectHeight: 7,
  useNaturalSize: false,

  // settings
  settings: {
    playerTime: SHOW_PLAYER_TIME,
    bigPlayButton: SHOW_BIG_PLAY_BUTTON,
    disabled: PLAYER_DISABLED,
    hoverPlay: ENABLE_HOVER_PLAY,
    hoverDelay: HOVER_DELAY,
  },
};

const { string, number, bool } = PropTypes;

VideoPlayer.propTypes = {
  className: string,
  rootClassName: string,
  videoWrapperClassName: string,
  videoClassName: string,
  controlsClassName: string,
  controlsTextClassName: string,
  video: propTypes.video,
  aspectWidth: number,
  aspectHeight: number,
  useNaturalSize: bool,

  // settings
  settings: shape({
    playerTime: bool,
    bigPlayButton: bool,
    disabled: bool,
    hoverPlay: bool,
    hoverDelay: number,
  }),
};

export default VideoPlayer;
